import { UserManagement_V1 } from "@rw1/auto-generated-clients";
import { useMemo } from "react";
import { ASSIGNED_VIA_DIRECT } from "../../../components/AssignedViaCell";
import { useAllExperiences } from "../../../hooks/useAllExperiences";
import { useExternalExperiences } from "../../../hooks/useExternalExperiences";
import { UserExperienceRow } from "./useUserExperiences";

interface UseUserExternalExperiencesParams {
  userId: string;
  enabled?: boolean;
  userGroups: UserManagement_V1.UserGroup[];
}

interface UseUserExternalExperiencesReturn {
  userExternalExperiences: UserExperienceRow[];
  isLoading: boolean;
}

const updateLongestExpiry = (
  current: Date | undefined,
  next: Date | undefined,
): Date | undefined => {
  if (next === undefined || current === undefined) {
    return undefined;
  }

  return next > current ? next : current;
};

export const useUserExternalExperiences = ({
  userId,
  userGroups,
}: UseUserExternalExperiencesParams): UseUserExternalExperiencesReturn => {
  const { userIdToAllocationMap, groupIdToAllocationMap } =
    useExternalExperiences();
  const { experiencesMap: allExperiencesMap } = useAllExperiences();

  const userExternalExperiences = useMemo(() => {
    const experiencesMap = new Map<string, UserExperienceRow>();

    const userDirectAllocations = userIdToAllocationMap.get(userId) ?? [];

    for (const { allocation, license } of userDirectAllocations) {
      const releaseChannelId = license.releaseChannelId;
      const experience = allExperiencesMap.get(releaseChannelId);

      const assignedVia = {
        source: ASSIGNED_VIA_DIRECT,
        sourceId: "",
      };

      experiencesMap.set(releaseChannelId, {
        id: releaseChannelId,
        title: experience?.friendlyName ?? license.licenseName,
        companyName: experience?.companyName ?? "-",
        assignedVia: [assignedVia],
        expirationDate: allocation.expiresAt,
        contentMetadataId: experience?.contentMetadataId ?? "",
      });
    }

    for (const userGroup of userGroups) {
      const groupAllocations =
        groupIdToAllocationMap.get(userGroup.userGroupId) ?? [];

      for (const { allocation, license } of groupAllocations) {
        const releaseChannelId = license.releaseChannelId;
        const experience = allExperiencesMap.get(releaseChannelId);

        const groupAssignedVia = {
          source: userGroup.userGroupName,
          sourceId: userGroup.userGroupId,
        };

        const existing = experiencesMap.get(releaseChannelId);

        if (existing) {
          existing.assignedVia.push(groupAssignedVia);

          existing.expirationDate = updateLongestExpiry(
            existing.expirationDate,
            allocation.expiresAt,
          );
        } else {
          experiencesMap.set(releaseChannelId, {
            id: releaseChannelId,
            title: experience?.friendlyName ?? license.licenseName,
            companyName: experience?.companyName ?? "-",
            assignedVia: [groupAssignedVia],
            expirationDate: allocation.expiresAt,
            contentMetadataId: experience?.contentMetadataId ?? "",
          });
        }
      }
    }

    return Array.from(experiencesMap.values());
  }, [
    userIdToAllocationMap,
    groupIdToAllocationMap,
    userId,
    userGroups,
    allExperiencesMap,
  ]);

  return {
    userExternalExperiences,
    isLoading: false,
  };
};
